import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Avatar,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import Select from 'react-select';
import { useSelector, useDispatch } from 'react-redux';
import { apiServices } from '../../../../services/apiServices';
import { QRCode as QRCodeWithLogo } from 'react-qrcode-logo';
import peacemakerImg from '../../../../assets/images/homeimages/peacemakerImg.png';
import { Configuration } from '../../../../assets/data/config';
import Swal from 'sweetalert2';
import UploadIcon from '@mui/icons-material/Upload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { countryCodes } from '../../../../utils/countryCodes';

// Helper function to convert DataURI to File
const dataURItoFile = (dataURI, filename) => {
  try {
    const arr = dataURI.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    
    // Create a proper File object with explicit property setting
    let file;
    try {
      // Try standard File constructor first
      file = new File([u8arr], filename, { 
        type: mime,
        lastModified: Date.now()
      });
      
      // Verify the File object was created correctly
      if (!file || !(file instanceof File) || file.size === 0 || typeof file.name !== 'string') {
        throw new Error('File constructor created invalid File object');
      }
    } catch (constructorError) {
      console.log('File constructor failed, creating manual File object:', constructorError);
      
      // Create a manual File-like object with proper properties
      file = Object.create(File.prototype);
      
      // Set properties explicitly
      Object.defineProperty(file, 'name', {
        value: filename,
        writable: false,
        enumerable: true,
        configurable: false
      });
      
      Object.defineProperty(file, 'type', {
        value: mime,
        writable: false,
        enumerable: true,
        configurable: false
      });
      
      Object.defineProperty(file, 'size', {
        value: u8arr.length,
        writable: false,
        enumerable: true,
        configurable: false
      });
      
      Object.defineProperty(file, 'lastModified', {
        value: Date.now(),
        writable: false,
        enumerable: true,
        configurable: false
      });
      
      // Add the data
      file._data = u8arr;
      
      // Add File methods
      file.stream = function() {
        return new ReadableStream({
          start(controller) {
            controller.enqueue(this._data);
            controller.close();
          }
        });
      };
      
      file.arrayBuffer = function() {
        return Promise.resolve(this._data.buffer);
      };
      
      file.text = function() {
        return Promise.resolve(new TextDecoder().decode(this._data));
      };
      
      // Ensure it's recognized as a File
      Object.setPrototypeOf(file, File.prototype);
    }
    
    console.log('dataURItoFile created:', {
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
      isFile: file instanceof File,
      nameType: typeof file.name,
      typeType: typeof file.type,
      sizeType: typeof file.size
    });
    
    return file;
  } catch (error) {
    console.error('Error in dataURItoFile:', error);
    throw error;
  }
};

// Helper function to mask email after 4 characters
const maskEmail = (email) => {
  if (!email || typeof email !== 'string') return '';
  
  const atIndex = email.indexOf('@');
  if (atIndex === -1) {
    // No @ symbol found, mask after 4 characters
    if (email.length <= 4) return email;
    return email.substring(0, 4) + '*'.repeat(Math.min(email.length - 4, 8));
  }
  
  const localPart = email.substring(0, atIndex);
  const domain = email.substring(atIndex);
  
  if (localPart.length <= 4) {
    // If local part is 4 characters or less, show it all
    return localPart + domain;
  }
  
  // Show first 4 characters, mask the rest until @
  const maskedLocal = localPart.substring(0, 4) + '*'.repeat(Math.min(localPart.length - 4, 8));
  return maskedLocal + domain;
};

const EditProfile = () => {
  // Get user data from Redux store
  const { user, isLoading } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    dateOfBirth: '',
    country: '',
    gender: '',
    mobile_email_flag: false // Email and website visibility on Certificate/Visiting card
  });

  // Country code state
  const [countryCode, setCountryCode] = useState('+91');

  // API integration states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [showFullPageLoader, setShowFullPageLoader] = useState(false);
  const [loaderMessage, setLoaderMessage] = useState('');

  // Profile picture upload states
  const [profileImage, setProfileImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null); // Store the file object for API
  const [uploading, setUploading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [regeneratingCertificate, setRegeneratingCertificate] = useState(false);

  // Logo image for QR code
  const [logoImage, setLogoImage] = useState(null);

  // Helper function to regenerate certificate after profile updates
  const regenerateCertificate = async () => {
    try {
      setRegeneratingCertificate(true);
      console.log('Regenerating certificate after profile update...');

      // Call the PUT endpoint to regenerate the certificate
      const updateResponse = await apiServices.putAPI(`update-certificate/${user.peacekeeper_id}`, {
        forceRegenerate: true
      });

      if (updateResponse.status === 200) {
        console.log('Certificate regenerated successfully after profile update');

        // Dispatch a custom event to notify other components that certificate has been updated
        const certificateUpdateEvent = new CustomEvent('certificateUpdated', {
          detail: {
            userId: user.userid,
            timestamp: new Date().toISOString(),
            message: 'Certificate has been regenerated with updated profile information'
          }
        });
        window.dispatchEvent(certificateUpdateEvent);

        // Show final success message
        setTimeout(() => {
          setSubmitMessage('Profile updated successfully! Your certificate has been regenerated with the latest information.');
        }, 1000);
      } else {
        console.warn('Certificate regeneration failed after profile update');
        // Show fallback message
        setTimeout(() => {
          setSubmitMessage('Profile updated successfully! Certificate regeneration may take a few moments.');
        }, 1000);
      }
    } catch (error) {
      console.error('Error regenerating certificate after profile update:', error);
      // Don't show error to user as this is a background process
    } finally {
      setRegeneratingCertificate(false);
    }
  };
  

  // Helper function to regenerate visiting card after profile updates
  const regenerateVisitingCard = async () => {
    try {
      console.log('Regenerating visiting card after profile update...');

      // Call the PUT endpoint to regenerate the visiting card
      const updateResponse = await apiServices.putAPI(`update-visiting-card/${user.peacekeeper_id}`, {
        forceRegenerate: true
      });

      if (updateResponse.status === 200) {
        console.log('Visiting card regenerated successfully after profile update');

        // Dispatch a custom event to notify other components that visiting card has been updated
        const visitingCardUpdateEvent = new CustomEvent('visitingCardUpdated', {
          detail: {
            userId: user.userid,
            timestamp: new Date().toISOString(),
            message: 'Visiting card has been regenerated with updated profile information'
          }
        });
        window.dispatchEvent(visitingCardUpdateEvent);

        console.log('Visiting card update event dispatched successfully');
      } else {
        console.warn('Visiting card regeneration failed after profile update');
      }
    } catch (error) {
      console.error('Error regenerating visiting card after profile update:', error);
      // Don't show error to user as this is a background process
    }
  };

  // Helper function to format ISO date to YYYY-MM-DD
  const formatDateForInput = (isoDate) => {
    if (!isoDate) return '';
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Load logo image for QR code
  useEffect(() => {
    setLogoImage(peacemakerImg);
  }, []);

  // Update form data when user data changes
  useEffect(() => {
    if (user) {
      console.log('User data:', user);
      const formattedDob = formatDateForInput(user.dob || user.date_of_birth);
      console.log('Original DOB:', user.dob || user.date_of_birth);
      console.log('Formatted DOB:', formattedDob);

      // Extract country code and phone number from user's phone
      const fullPhone = user.phone || user.mobile || '';
      let extractedCountryCode = '+91'; // Default
      let phoneNumber = fullPhone;

      // Try to extract country code from phone number
      if (fullPhone) {
        // First, try to find matching country code with "+" prefix
        let matchedCountryCode = countryCodes.find(cc => fullPhone.startsWith(cc.code));
        
        // If not found, try matching without "+" prefix (in case phone number doesn't have +)
        if (!matchedCountryCode) {
          matchedCountryCode = countryCodes.find(cc => {
            const codeWithoutPlus = cc.code.substring(1); // Remove the "+" from country code
            return fullPhone.startsWith(codeWithoutPlus);
          });
          
          if (matchedCountryCode) {
            extractedCountryCode = matchedCountryCode.code;
            // Remove the country code without "+" from the phone number
            phoneNumber = fullPhone.substring(matchedCountryCode.code.length - 1); // -1 because we removed the "+"
          }
        } else {
          // Found with "+" prefix
          extractedCountryCode = matchedCountryCode.code;
          phoneNumber = fullPhone.substring(matchedCountryCode.code.length);
        }
      }

      setCountryCode(extractedCountryCode);

      setFormData({
        fullName: user.username || '',
        phoneNumber: phoneNumber,
        email: user.email || '',
        dateOfBirth: formattedDob,
        country: user.country || '',
        gender: user.gender || '',
        mobile_email_flag: user.mobile_email_flag === 'Y' || user.mobile_email_flag === true || false
      });

      // Clear any previous error messages when user data updates
      setSubmitError('');
    }
  }, [user]);

  // Fetch profile image when component mounts
  useEffect(() => {
    if (user?.userid) {
      // Use cache busting on initial load to prevent Safari from showing cached images
      fetchProfileImage(true);
    }
  }, [user?.userid]);

  // Listen for profile update events from DashboardSidebar
  useEffect(() => {
    const handleProfileImageUpdate = async (event) => {
      console.log('Profile image update event received in EditProfile:', event.detail);

      // Check if the event is for the current user
      if (event.detail.userId === user?.userid) {
        console.log('Refreshing profile image in EditProfile after profile image update...');

        // Wait a moment for the backend to process, then refresh with cache busting for Safari
        setTimeout(async () => {
          await fetchProfileImage(true);
        }, 1000); // Wait 1 second for backend processing
      }
    };

    const handleProfileInfoUpdate = async (event) => {
      console.log('Profile info update event received in EditProfile:', event.detail);

      // Check if the event is for the current user
      if (event.detail.userId === user?.userid) {
        console.log('Refreshing profile image in EditProfile after profile info update...');

        // Wait a moment for the backend to process, then refresh with cache busting for Safari
        setTimeout(async () => {
          await fetchProfileImage(true);
        }, 1000); // Wait 1 second for backend processing
      }
    };

    // Add event listeners
    window.addEventListener('profileImageUpdated', handleProfileImageUpdate);
    window.addEventListener('profileInfoUpdated', handleProfileInfoUpdate);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('profileImageUpdated', handleProfileImageUpdate);
      window.removeEventListener('profileInfoUpdated', handleProfileInfoUpdate);
    };
  }, [user?.userid]);

  // Cleanup profile image URL when component unmounts
  useEffect(() => {
    return () => {
      if (profileImage && profileImage.startsWith('blob:')) {
        URL.revokeObjectURL(profileImage);
      }
    };
  }, [profileImage]);

  // Safety timeout to hide loader if stuck
  useEffect(() => {
    if (showFullPageLoader) {
      const timeout = setTimeout(() => {
        console.log('Loader timeout - hiding loader');
        setShowFullPageLoader(false);
        setLoaderMessage('');
      }, 30000); // 30 seconds timeout

      return () => clearTimeout(timeout);
    }
  }, [showFullPageLoader]);

  // Debug loader state changes
  useEffect(() => {
    console.log('Loader state changed:', { showFullPageLoader, loaderMessage });
  }, [showFullPageLoader, loaderMessage]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Handle country code change
  const handleCountryCodeChange = (selectedOption) => {
    const newCountryCode = selectedOption ? selectedOption.code : '+91';
    setCountryCode(newCountryCode);
  };

  // Profile picture upload functions
  const fetchProfileImage = async (forceRefresh = false) => {
    if (!user?.userid) return;

    try {
      // Clear existing profile image URL to prevent Safari caching issues
      if (profileImage && profileImage.startsWith('blob:')) {
        URL.revokeObjectURL(profileImage);
        setProfileImage(null);
      }

      let response;
      try {
        // Add cache busting for Safari - append timestamp to URL
        const cacheBuster = forceRefresh ? `?t=${Date.now()}` : '';
        const apiUrl = `profile-image/${user.userid}${cacheBuster}`;
        
        // Try to get as blob first
        response = await apiServices.getBlobAPI(apiUrl);
        console.log('Profile image API response (getBlobAPI):', response);
      } catch (blobError) {
        // Fallback to regular API call with cache busting
        const cacheBuster = forceRefresh ? `?t=${Date.now()}` : '';
        const apiUrl = `profile-image/${user.userid}${cacheBuster}`;
        
        response = await apiServices.getAPI(apiUrl);
        console.log('Profile image API response (getAPI):', response);
      }

      if (response.status === 200 && response.data) {
        if (response.data instanceof Blob) {
          // Handle blob response
          const imageUrl = URL.createObjectURL(response.data);
          setProfileImage(imageUrl);
          console.log('Profile image state set to:', imageUrl);
        } else if (response.data && typeof response.data === 'string') {
          // Handle string URL response - add cache busting for Safari
          const imageUrl = forceRefresh ? `${response.data}?t=${Date.now()}` : response.data;
          setProfileImage(imageUrl);
          console.log('Profile image state set to:', imageUrl);
        } else {
          setProfileImage(peacemakerImg);
        }
      } else {
        setProfileImage(peacemakerImg);
      }
    } catch (error) {
      console.error('Error fetching profile image:', error);
      setProfileImage(peacemakerImg);
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    console.log("file", file)
    if (file) {
      // Validate file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        Swal.fire({
          title: 'File Too Large',
          text: 'Please select an image smaller than 2MB',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        Swal.fire({
          title: 'Invalid File Type',
          text: 'Please select a valid image file (JPG, PNG, etc.)',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return;
      }

      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);
    }
  };

  // Camera functionality
  const handleCameraCapture = () => {
    console.log('Camera capture initiated');
    console.log('Cordova available:', !!window.cordova);
    console.log('navigator.camera available:', !!navigator.camera);
    console.log('window.Camera available:', !!window.Camera);
    console.log('Device platform:', window.device?.platform || 'unknown');
    console.log('User agent:', navigator.userAgent);

    // Check if Cordova camera plugin is available
    if (window.cordova && navigator.camera && window.Camera) {
      console.log('Camera plugin detected, opening camera...');

      // Show loading message
      Swal.fire({
        title: 'Opening Camera...',
        text: 'Please wait while the camera is being prepared.',
        icon: 'info',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const options = {
        quality: 50, // Reduced quality for better Android compatibility
        destinationType: window.Camera.DestinationType.DATA_URL, // Use base64 data URL
        sourceType: window.Camera.PictureSourceType.CAMERA,
        allowEdit: false, // Disabled for better Android compatibility
        encodingType: window.Camera.EncodingType.JPEG,
        targetWidth: 600, // Reduced size for better Android compatibility
        targetHeight: 600,
        correctOrientation: true,
        saveToPhotoAlbum: false,
        cameraDirection: window.Camera.Direction.BACK, // Use back camera
        mediaType: window.Camera.MediaType.PICTURE // Ensure it's a picture
      };

      console.log('Camera options:', options);

      // Add timeout for camera operation
      const cameraTimeout = setTimeout(() => {
        Swal.close();
        Swal.fire({
          title: 'Camera Timeout',
          text: 'Camera took too long to respond. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }, 30000); // 30 second timeout

      let capturedFile = null;
      navigator.camera.getPicture(
        (imageData) => {
          // Success callback - now we get base64 data
          console.log('Camera captured base64 data length:', imageData.length);
          
          // Clear timeout
          clearTimeout(cameraTimeout);
          Swal.close();
          
          // Handle async operation inside regular callback
          const processImage = async () => {
            try {
              // Create dataURI for preview (direct base64 usage)
              const dataURI = imageData.startsWith("data:")
                ? imageData
                : "data:image/jpeg;base64," + imageData;

              console.log("Camera base64 data length:", imageData.length);
              console.log("Camera dataURI created for preview");

              // Set preview directly using base64 data
              setPreviewImage(dataURI);
              console.log("Camera preview created successfully using base64");
      
              // Set the file to the hidden input (for backward compatibility)
              const fileInput = document.getElementById('edit-profile-upload-input');
              if (fileInput) {
                // Remove existing files if already present
                if (fileInput.files && fileInput.files.length > 0) {
                  console.log("Removing existing files from input");
                  fileInput.value = '';
                }
                console.log("Camera file set to input successfully");
              }
      
              console.log("File ready:", capturedFile);
            } catch (err) {
              console.error("Error processing camera image:", err);
              Swal.fire({
                title: "Error",
                text: "Failed to process captured image",
                icon: "error",
                confirmButtonText: "OK"
              });
            }
          };
          
          // Call the async function
          processImage();
        },
        (error) => {
          // Error callback
          console.error("Camera error:", error);
          clearTimeout(cameraTimeout);
          Swal.close();
          
          // Handle specific error cases
          let errorTitle = 'Camera Error';
          let errorMessage = 'Could not capture image. Please try again.';

          if (error === 'No Image Selected' || error === 'User cancelled') {
            // Silent cancellation - no error message shown
            return;
          } else if (error === 'Camera not available') {
            errorTitle = 'Camera Not Available';
            errorMessage = 'Camera is not available on this device. Please use the file upload option instead.';
          } else if (error === 'Permission denied') {
            errorTitle = 'Permission Denied';
            errorMessage = 'Camera permission was denied. Please enable camera access in your device settings.';
          }

          Swal.fire({
            title: errorTitle,
            text: errorMessage,
            icon: 'error',
            confirmButtonText: 'OK'
          });
        },
        options
      );
    } else {
      // Fallback for web browsers or if Cordova is not available
      console.log('Camera plugin not available, showing fallback message');
      Swal.fire({
        title: 'Camera Not Available',
        text: 'Camera functionality is only available on mobile devices. Please use the file upload option instead.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleCameraFromGallery = () => {
    // Check if Cordova camera plugin is available
    if (window.cordova && window.cordova.plugins && navigator.camera) {
      const options = {
        quality: 80,
        destinationType: window.Camera.DestinationType.DATA_URL, // Use base64 data URL
        sourceType: window.Camera.PictureSourceType.PHOTOLIBRARY,
        allowEdit: true,
        encodingType: window.Camera.EncodingType.JPEG,
        targetWidth: 800,
        targetHeight: 800,
        correctOrientation: true
      };

      navigator.camera.getPicture(
        (imageData) => {
          // Success callback - now we get base64 data
          console.log('Gallery selection successful');
          console.log('Gallery base64 data length:', imageData.length);

          // Handle async operation inside regular callback
          const processImage = async () => {
            try {
              // Create dataURI for preview (direct base64 usage)
              const dataURI = imageData.startsWith("data:")
                ? imageData
                : "data:image/jpeg;base64," + imageData;

              console.log("Gallery base64 data length:", imageData.length);
              console.log("Gallery dataURI created for preview");

              // Set preview directly using base64 data
              setPreviewImage(dataURI);
              console.log("Gallery preview created successfully using base64");

              // Convert base64 to File object for API usage
              const finalFile = dataURItoFile(dataURI, "gallery-selection.jpg");

              console.log("Gallery File:", finalFile);
              console.log("Gallery Size:", finalFile.size);
              console.log("Gallery Type:", finalFile.type);

              // Store file object in state for API usage
              setSelectedFile(finalFile);
              console.log("Gallery file stored in state:", finalFile);

              // Store the file in a hidden input for upload (for backward compatibility)
              const fileInput = document.getElementById('edit-profile-upload-input');
              if (fileInput) {
                // Remove existing files if already present
                if (fileInput.files && fileInput.files.length > 0) {
                  console.log("Removing existing files from input");
                  fileInput.value = '';
                }
                fileInput.files = [finalFile];
                console.log("Gallery file set to input successfully");
              }

            } catch (err) {
              console.error("Error processing gallery image:", err);
              Swal.fire({
                title: "Error",
                text: "Failed to process selected image",
                icon: "error",
                confirmButtonText: "OK"
              });
            }
          };
          
          // Call the async function
          processImage();

          // No success message - image is directly set and ready for upload
        },
        (error) => {
          // Error callback
          console.error('Gallery error:', error);

          // Handle specific error cases
          let errorTitle = 'Gallery Error';
          let errorMessage = 'Failed to select photo from gallery. Please try again or use the file upload option.';

          if (error === 'No Image Selected' || error === 'User cancelled') {
            // Silent cancellation - no error message shown
            return;
          } else if (error === 'Gallery not available') {
            errorTitle = 'Gallery Not Available';
            errorMessage = 'Gallery is not available on this device. Please use the file upload option instead.';
          } else if (error === 'Permission denied') {
            errorTitle = 'Gallery Permission Denied';
            errorMessage = 'Gallery permission was denied. Please enable photo library access in your device settings and try again.';
          }

          Swal.fire({
            title: errorTitle,
            text: errorMessage,
            icon: error === 'No Image Selected' || error === 'User cancelled' ? 'info' : 'error',
            confirmButtonText: 'OK'
          });
        },
        options
      );
    } else {
      // Fallback for web browsers - trigger file input
      const fileInput = document.getElementById('edit-profile-upload-input');
      if (fileInput) {
        fileInput.click();
      } else {
        Swal.fire({
          title: 'Gallery Not Available',
          text: 'Gallery functionality is only available on mobile devices. Please use the file upload option instead.',
          icon: 'info',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const handleUploadProfileImage = async () => {
    if (!previewImage || !user?.userid) {
      console.error('No image selected or user ID missing');
      return;
    }

    setUploading(true);
    try {
      // Create FormData for the upload
      const formData = new FormData();

      // Get the original file from the input element instead of converting from URL
      const fileInput = document.getElementById('edit-profile-upload-input');
      const originalFile = fileInput.files[0];
      console.log("Preview image:", previewImage);


      if (!originalFile) {
        console.log('No file found in input element, using base64 preview image');
        
        // Check if previewImage is base64 data
        if (!previewImage || (!previewImage.startsWith('data:') && !previewImage.startsWith('blob:'))) {
          console.error('No valid preview image available for base64 upload');
          Swal.fire({
            title: 'Upload Error',
            text: 'No image available for upload. Please select an image file.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
          return;
        }

        // Use base64 API endpoint
        const base64Data = previewImage.startsWith('data:') 
          ? previewImage 
          : `data:image/jpeg;base64,${previewImage}`;

        console.log("Using base64 data for upload:", base64Data.substring(0, 100) + '...');

        const base64Payload = {
          userId: user.userid,
          imageType: 'profile',
          base64Image: base64Data
        };

        // Upload using base64 API
        const uploadResponse = await apiServices.postAPI('upload-profile-image-base64', base64Payload);
        
        if (uploadResponse.status === 200 || uploadResponse.status === 201) {
          console.log('Profile image uploaded successfully via base64 API');
        } else {
          throw new Error(`Base64 upload failed with status: ${uploadResponse.status}`);
        }
      } else {
        console.log("Original file:", originalFile);

        // Use regular FormData upload
        formData.append('profileImage', originalFile);
        formData.append('userId', user.userid);
        formData.append('imageType', 'profile');

        // Upload the image
        const uploadResponse = await apiServices.postMultiPartFormDataAPI('upload-profile-image', formData);
        
        if (uploadResponse.status === 200 || uploadResponse.status === 201) {
          console.log('Profile image uploaded successfully via FormData API');
        } else {
          throw new Error(`FormData upload failed with status: ${uploadResponse.status}`);
        }
      }

      // Both upload methods have been handled above, continue with success flow
      console.log('Profile image uploaded successfully');

      // Dispatch a custom event to notify other components that profile image has been updated
      const profileImageUpdateEvent = new CustomEvent('profileImageUpdated', {
        detail: {
          userId: user.userid,
          timestamp: new Date().toISOString(),
          message: 'Profile image has been updated successfully'
        }
      });
      window.dispatchEvent(profileImageUpdateEvent);

      // Clear the preview image first
      setPreviewImage(null);

      // Small delay for Safari to process the image update
      await new Promise(resolve => setTimeout(resolve, 500));

      // Refresh the profile image from the API with cache busting for Safari
      await fetchProfileImage(true);

      // Show success message
      setShowSuccessMessage(true);
      setSubmitMessage('Profile image updated successfully! Your certificate and visiting card will be regenerated with the new image.');

      // Show certificate regeneration status
      setTimeout(() => {
        setSubmitMessage('Profile image updated successfully! Regenerating your certificate and visiting card...');
      }, 2000);

      // Regenerate certificate after profile image update (with small delay to ensure backend processing)
      setTimeout(async () => {
        await regenerateCertificate();
      }, 1000);

      // Regenerate visiting card after profile image update (with small delay to ensure backend processing)
      setTimeout(async () => {
        await regenerateVisitingCard();
      }, 1000);

      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    } catch (error) {
      console.error('Error uploading profile image:', error);
      // Check if it's an API error with response data
      if (error.response?.data) {
        Swal.fire({
          title: 'Upload Error',
          text: `Error uploading profile image: ${error.response.data.message || error.response.data}`,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } else {
        Swal.fire({
          title: 'Upload Error',
          text: 'Error uploading profile image. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset previous messages
    setSubmitMessage('');
    setSubmitError('');

    // Validate required fields
    if (!formData.fullName.trim()) {
      setSubmitError('Full Name is required');
      return;
    }

    if (!formData.phoneNumber.trim()) {
      setSubmitError('Phone Number is required');
      return;
    }

    if (!formData.dateOfBirth) {
      setSubmitError('Date of Birth is required');
      return;
    }

    setIsSubmitting(true);

    // Show full page loader
    setShowFullPageLoader(true);
    setLoaderMessage('Updating your profile...');

    try {
      let profileInfoUpdated = false;
      let profileImageUpdated = false;
      let successMessage = '';

      // First, handle profile image upload if there's a preview image
      if (previewImage && user?.userid) {
        setLoaderMessage('Uploading profile image...');
        
        try {
          // Create FormData for the upload
          const uploadFormData = new FormData();

          // Get the original file from the input element
          const fileInput = document.getElementById('edit-profile-upload-input');
          const originalFile = fileInput.files[0];

          if (originalFile) {
            // Use regular FormData upload
            uploadFormData.append('profileImage', originalFile);
            uploadFormData.append('userId', user.userid);
            uploadFormData.append('imageType', 'profile');

            // Upload the image
            const uploadResponse = await apiServices.postMultiPartFormDataAPI('upload-profile-image', uploadFormData);

            if (uploadResponse.status === 200 || uploadResponse.status === 201) {
              console.log('Profile image uploaded successfully');
              profileImageUpdated = true;
            } else {
              console.error('Failed to upload profile image:', uploadResponse.statusText || uploadResponse.status);
              throw new Error('Failed to upload profile image');
            }
          } else {
            // Check if previewImage is base64 data
            if (previewImage && (previewImage.startsWith('data:') || previewImage.startsWith('blob:'))) {
              // Use base64 API endpoint
              const base64Data = previewImage.startsWith('data:') 
                ? previewImage 
                : `data:image/jpeg;base64,${previewImage}`;

              console.log("Using base64 data for upload:", base64Data.substring(0, 100) + '...');

              const base64Payload = {
                userId: user.userid,
                imageType: 'profile',
                base64Image: base64Data
              };

              // Upload using base64 API
              const uploadResponse = await apiServices.postAPI('upload-profile-image-base64', base64Payload);
              
              if (uploadResponse.status === 200 || uploadResponse.status === 201) {
                console.log('Profile image uploaded successfully via base64 API');
                profileImageUpdated = true;
              } else {
                throw new Error(`Base64 upload failed with status: ${uploadResponse.status}`);
              }
            } else {
              console.error('No valid file or base64 data for upload');
              throw new Error('No image available for upload');
            }
          }

          if (profileImageUpdated) {
            // Dispatch a custom event to notify other components that profile image has been updated
            const profileImageUpdateEvent = new CustomEvent('profileImageUpdated', {
              detail: {
                userId: user.userid,
                timestamp: new Date().toISOString(),
                message: 'Profile image has been updated successfully'
              }
            });
            window.dispatchEvent(profileImageUpdateEvent);

            // Clear the preview image first
            setPreviewImage(null);

            // Small delay for Safari to process the image update
            await new Promise(resolve => setTimeout(resolve, 500));

            // Refresh the profile image from the API with cache busting for Safari
            await fetchProfileImage(true);
          }

        } catch (error) {
          console.error('Error uploading profile image:', error);
          throw new Error('Failed to upload profile image. Please try again.');
        }
      }

      // Then, handle profile information update
      setLoaderMessage('Updating profile information...');
      
      // Prepare data for API call
      const updateData = {
        email: formData.email,
        username: formData.fullName,
        dob: formData.dateOfBirth,
        mobile: countryCode + formData.phoneNumber,
        gender: formData.gender,
        mobile_email_flag: formData.mobile_email_flag ? "Y" : "N" // Convert boolean to "Y" or "N"
      };

      console.log('Data to be sent to API:', updateData);

      // Make API call
      const response = await apiServices.putAPI('update-user-info', updateData);

      console.log('API Response:', response);

      if (response.data.message === 'User information updated successfully') {
        profileInfoUpdated = true;
        
        // Update Redux state with new user information
        if (response.data.user) {
          const updatedUser = {
            ...user, // Keep existing user data
            username: response.data.user.username,
            dob: response.data.user.dob,
            mobile: response.data.user.mobile,
            phone: response.data.user.mobile, // Also update phone field for compatibility
            // Update other fields that might have changed
            ...response.data.user
          };

          console.log('Updating Redux state with:', updatedUser);

          // Dispatch action to update user in Redux store
          dispatch({
            type: 'auth/setUser',
            payload: updatedUser
          });

          // Dispatch a custom event to notify other components that profile information has been updated
          const profileInfoUpdateEvent = new CustomEvent('profileInfoUpdated', {
            detail: {
              userId: user.userid,
              timestamp: new Date().toISOString(),
              message: 'Profile information has been updated successfully'
            }
          });
          window.dispatchEvent(profileInfoUpdateEvent);
        }
      }

      // Prepare success message based on what was updated
      if (profileInfoUpdated && profileImageUpdated) {
        successMessage = 'Profile information and image updated successfully! Your certificate will be regenerated with the new information and image.';
      } else if (profileInfoUpdated) {
        successMessage = 'Profile information updated successfully! Your certificate will be regenerated with the new information.';
      } else if (profileImageUpdated) {
        successMessage = 'Profile image updated successfully! Your certificate will be regenerated with the new image.';
      } else {
        successMessage = 'Profile updated successfully!';
      }

      // Show SweetAlert success message
      Swal.fire({
        title: 'Success!',
        text: successMessage,
        icon: 'success',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });

      // Set success message for the Alert component as well (for backward compatibility)
      setSubmitMessage(successMessage);

      // Show certificate regeneration status
      setTimeout(() => {
        setSubmitMessage('Profile updated successfully! Regenerating your certificate...');
      }, 2000);

      // Regenerate certificate after updates (with small delay to ensure backend processing)
      if (profileInfoUpdated || profileImageUpdated) {
        setTimeout(async () => {
          await regenerateCertificate();
        }, 1000);

        // Regenerate visiting card after updates (with small delay to ensure backend processing)
        setTimeout(async () => {
          await regenerateVisitingCard();
        }, 1000);
      }

      // Reset messages after 3 seconds
      setTimeout(() => {
        setSubmitMessage('');
      }, 3000);

    } catch (error) {
      console.error('Error updating profile:', error);
      setSubmitError(error.response?.data?.message || error.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsSubmitting(false);
      // Hide full page loader
      setShowFullPageLoader(false);
      setLoaderMessage('');
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Full Page Loader */}
      {showFullPageLoader && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            color: 'white',
          }}
        >
          <Box
            sx={{
              width: 50,
              height: 50,
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #214F7D',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              mb: 2,
            }}
          />
          <Typography variant="h6" sx={{ textAlign: 'center' }}>
            {loaderMessage}
          </Typography>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </Box>
      )}

      {/* Split Layout: 4-4-4 Grid */}
      {user && !isLoading && (
        <Grid container spacing={3} sx={{ mb: 4, width: '100%' }}>
          
          {/* Profile Picture - Mobile First */}
          <Grid size={{ xs: 12, md: 6, lg: 5 }} sx={{ order: { xs: 1, md: 2 } }}>
            <Typography fontWeight="600" sx={{ mb: 2, color: 'text.primary' }}>
              Profile Picture
            </Typography>
            <Typography sx={{ mb: 3, color: 'info.main', fontSize: '14px' }}>
              💡 <strong>Certificate Auto-Update:</strong> Whenever you update your profile details or profile picture, your I Am Peacekeeper Certificate will automatically regenerate to reflect the latest changes. <br />
              Please note: Your <strong>Peacekeeper ID, Email, and Country of Origin</strong> are read-only and managed through your account settings. All other profile information, including your profile image (upload from file, camera, or gallery), can be edited and will be reflected in your certificate.
            </Typography>

            <Box
              sx={{
                border: '2px dashed #cbd5e1',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                position: 'relative',
                bgcolor: uploading ? '#f5f5f5' : '#f9fafb',
                opacity: uploading ? 0.7 : 1,
                width: '100%',
                maxWidth: '100%',
                height: 220,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                overflow: 'hidden',
                minWidth: 0
              }}
              onClick={() => !uploading && document.getElementById('edit-profile-upload-input').click()}
            >
              {uploading ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4
                }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #214F7D',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Uploading...
                  </Typography>
                </Box>
              ) : (profileImage || previewImage) ? (
                <Box
                  component="img"
                  src={previewImage || profileImage}
                  alt="Profile Preview"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 1
                  }}
                />
              ) : (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 4
                }}>
                  <Box
                    component="img"
                    src="https://www.svgrepo.com/show/508699/image.svg"
                    alt="upload icon"
                    sx={{ width: 64, height: 64, mb: 2 }}
                  />
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <Typography component="span" color="primary" fontWeight="bold">
                      Click to upload
                    </Typography> or drag and drop
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PNG, JPG, JPEG up to 2MB
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Hidden file input */}
            <input
              type="file"
              id="edit-profile-upload-input"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: 'none' }}
            />

            {/* Image selection buttons when profile image exists but no preview */}
            {profileImage && !previewImage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    onClick={() => document.getElementById('edit-profile-upload-input').click()}
                    disabled={uploading}
                    startIcon={<UploadIcon />}
                    sx={{
                      borderColor: '#214F7D',
                      color: '#214F7D',
                      borderRadius: "50px",
                      px: 3,
                      '&:hover': {
                        borderColor: '#1a3f63',
                        backgroundColor: 'rgba(33, 79, 125, 0.04)',
                      },
                      '&:disabled': {
                        borderColor: '#ccc',
                        color: '#ccc'
                      }
                    }}
                  >
                    Upload File
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleCameraCapture}
                    disabled={uploading}
                    startIcon={<CameraAltIcon />}
                    sx={{
                      borderColor: '#3A8139',
                      color: '#3A8139',
                      borderRadius: "50px",
                      px: 3,
                      '&:hover': {
                        borderColor: '#2d6a2e',
                        backgroundColor: 'rgba(58, 129, 57, 0.04)',
                      },
                      '&:disabled': {
                        borderColor: '#ccc',
                        color: '#ccc'
                      }
                    }}
                  >
                    Take Photo
                  </Button>
                </Box>
              </Box>
            )}

            {/* Add Image Button - When no profile image exists */}
            {!profileImage && !previewImage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    onClick={() => document.getElementById('edit-profile-upload-input').click()}
                    disabled={uploading}
                    startIcon={<UploadIcon />}
                    sx={{
                      borderColor: '#214F7D',
                      color: '#214F7D',
                      borderRadius: "50px",
                      px: 3,
                      '&:hover': {
                        borderColor: '#1a3f63',
                        backgroundColor: 'rgba(33, 79, 125, 0.04)',
                      },
                      '&:disabled': {
                        borderColor: '#ccc',
                        color: '#ccc'
                      }
                    }}
                  >
                    Upload File
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleCameraCapture}
                    disabled={uploading}
                    startIcon={<CameraAltIcon />}
                    sx={{
                      borderColor: '#3A8139',
                      color: '#3A8139',
                      borderRadius: "50px",
                      px: 3,
                      '&:hover': {
                        borderColor: '#2d6a2e',
                        backgroundColor: 'rgba(58, 129, 57, 0.04)',
                      },
                      '&:disabled': {
                        borderColor: '#ccc',
                        color: '#ccc'
                      }
                    }}
                  >
                    Take Photo
                  </Button>
                </Box>
              </Box>
            )}

            {/* Image selection buttons when preview exists */}
            {previewImage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="success.main" sx={{ mb: 2, fontSize: '14px', fontWeight: 'bold' }}>
                  ✅ New image selected! You can change it below or save to confirm.
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => document.getElementById('edit-profile-upload-input').click()}
                    disabled={uploading}
                    startIcon={<UploadIcon />}
                    sx={{
                      borderColor: '#214F7D',
                      color: '#214F7D',
                      borderRadius: "50px",
                      px: 3,
                      '&:hover': {
                        borderColor: '#1a3f63',
                        backgroundColor: 'rgba(33, 79, 125, 0.04)',
                      },
                      '&:disabled': {
                        borderColor: '#ccc',
                        color: '#ccc'
                      }
                    }}
                  >
                    Upload File
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleCameraCapture}
                    disabled={uploading}
                    startIcon={<CameraAltIcon />}
                    sx={{
                      borderColor: '#3A8139',
                      color: '#3A8139',
                      borderRadius: "50px",
                      px: 3,
                      '&:hover': {
                        borderColor: '#2d6a2e',
                        backgroundColor: 'rgba(58, 129, 57, 0.04)',
                      },
                      '&:disabled': {
                        borderColor: '#ccc',
                        color: '#ccc'
                      }
                    }}
                  >
                    Take Photo
                  </Button>
                </Box>
                
                <Typography variant="body2" color="info.main" sx={{ mb: 2, fontSize: '14px' }}>
                  💡 Click "Save Changes" below to upload your profile picture along with any profile information updates.
                </Typography>
              </Box>
            )}

            {/* Success Message */}
            {showSuccessMessage && (
              <Box sx={{
                mt: 2,
                p: 2,
                bgcolor: '#d4edda',
                borderRadius: 1,
                border: '1px solid #c3e6cb',
                color: '#155724',
                maxWidth: 400,
                mx: 'auto'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  ✓ Profile image uploaded successfully!
                </Typography>
              </Box>
            )}
          </Grid>

          {/* First 4 columns: Edit Fields */}
          <Grid size={{ xs: 12, md: 6, lg: 7 }} sx={{ order: { xs: 2, md: 1 } }}>
            <Typography fontWeight="800" sx={{ mb: 3, color: 'text.primary' }}>
              Profile Information
            </Typography>

            

            {/* Information Notice */}
            <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Note: <strong>I Am Peacekeeper ID</strong>, Email and Country of Origin are read-only and managed by your account
                settings. You can edit other profile information below.
              </Typography>
            </Box>

            <form onSubmit={handleSubmit} className='editProfileForms' style={{ width: '100%' }}>
              {/* First Row - 2 fields */}
              <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
                {/* Peacekeeper ID */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left", mb: 1 }}>I Am Peacekeeper ID </Typography>
                  <TextField
                    fullWidth
                    name="peacekeeperId"
                    placeholder="I Am Peacekeeper ID "
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 1 }}
                    value={user.peacekeeper_id || 'Not Available'}
                    disabled
                    helperText="Your unique I Am Peacekeeper identifier"
                  />
                </Grid>

                {/* Email */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left", mb: 1 }}>Email</Typography>
                  <TextField
                    fullWidth
                    name="email"
                    placeholder="Enter Email"
                    type="email"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 1 }}
                    value={maskEmail(formData.email)}
                    onChange={handleChange}
                    disabled
                    helperText="Your email address (managed through your account settings)"
                  />
                </Grid>
              </Grid>

              {/* Second Row - 2 fields */}
              <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
                {/* Full Name */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left", mb: 1 }}>Full Name *</Typography>
                  <TextField
                    fullWidth
                    name="fullName"
                    placeholder="Enter Full Name"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 1 }}
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    helperText="Enter your full name"
                  />
                </Grid>

                {/* Phone Number */}
                {/* <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left", mb: 1 }}>Phone Number *</Typography>
                  <TextField
                    fullWidth
                    name="phoneNumber"
                    placeholder="Enter Phone Number"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 1 }}
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    helperText="Enter your phone number"
                  />
                </Grid> */}


                <Grid size={{ xs: 12, md: 6 }}>
                                  <Typography sx={{ textAlign: "left" }}>Phone Number</Typography>
                                  <Box sx={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
                                    {/* Country Code Dropdown */}
                                    <Select
                                      className="country-code-select"
                                      classNamePrefix="country-code-select"
                                      placeholder="Code"
                                      isSearchable={true}
                                      value={countryCodes.find(cc => cc.code === countryCode) || countryCodes.find(cc => cc.code === '+91')}
                                      onChange={handleCountryCodeChange}
                                      options={countryCodes}
                                      getOptionLabel={(option) => option.code}
                                      getOptionValue={(option) => option.code}
                                      formatOptionLabel={(option) => (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <img 
                                            src={`https://flagcdn.com/w20/${option.country}.png`}
                                            alt={option.name}
                                            style={{ width: '20px', height: '15px', objectFit: 'cover', borderRadius: '2px' }}
                                          />
                                          <span>{option.code}</span>
                                        </Box>
                                      )}
                                      styles={{
                                        control: (provided, state) => ({
                                          ...provided,
                                          minHeight: '40px',
                                          width: '110px',
                                          border: '1px solid #ccc',
                                          borderRadius: '4px 0 0 4px',
                                          borderRight: 'none',
                                          boxShadow: state.isFocused ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none',
                                          '&:hover': {
                                            borderColor: '#999'
                                          }
                                        }),
                                        option: (provided, state) => ({
                                          ...provided,
                                          backgroundColor: state.isSelected ? '#1976d2' : state.isFocused ? '#f5f5f5' : 'white',
                                          color: state.isSelected ? 'white' : '#333',
                                          textAlign: 'left',
                                          justifyContent: 'flex-start',
                                          padding: '6px 5px',
                                          cursor: 'pointer',
                                          fontSize: '14px',
                                          '&:hover': {
                                            backgroundColor: state.isSelected ? '#1976d2' : '#f0f0f0'
                                          }
                                        }),
                                        menu: (provided) => ({
                                          ...provided,
                                          zIndex: 9999,
                                          marginTop: '4px',
                                          width: '150px'
                                        }),
                                        menuList: (provided) => ({
                                          ...provided,
                                          textAlign: 'left',
                                          padding: '4px 0'
                                        }),
                                        singleValue: (provided) => ({
                                          ...provided,
                                          color: '#333',
                                          textAlign: 'left',
                                          fontSize: '14px'
                                        }),
                                        placeholder: (provided) => ({
                                          ...provided,
                                          color: '#999',
                                          textAlign: 'left',
                                          fontSize: '14px'
                                        }),
                                        noOptionsMessage: (provided) => ({
                                          ...provided,
                                          textAlign: 'left',
                                          padding: '8px 12px',
                                          fontSize: '14px'
                                        }),
                                        input: (provided) => ({
                                          ...provided,
                                          textAlign: 'left',
                                          fontSize: '14px'
                                        }),
                                        valueContainer: (provided) => ({
                                          ...provided,
                                          textAlign: 'left',
                                          padding: '0 8px'
                                        })
                                      }}
                                    />
                                    {/* Phone Number Input */}
                                    <TextField
                                      fullWidth
                                      name="phoneNumber"
                                      label=""
                                      type="tel"
                                      required
                                      variant="outlined"
                                      margin="dense"
                                      size="small"
                                      placeholder="Enter phone number"
                                      sx={{ 
                                        mb: 2,
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: '0 4px 4px 0',
                                          borderLeft: 'none'
                                        }
                                      }}
                                      value={formData.phoneNumber}
                                      onChange={handleChange}
                                      helperText="Enter your phone number"
                                    />
                                  </Box>
                                </Grid>
              </Grid>

              {/* Third Row - 2 fields */}
              <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
                {/* Date of Birth */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left", mb: 1 }}>Date of Birth *</Typography>
                  <TextField
                    fullWidth
                    name="dateOfBirth"
                    type="date"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 1 }}
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText="Enter your date of birth"
                  />
                </Grid>

                {/* Country */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left", mb: 1 }}>Country</Typography>
                  <TextField
                    fullWidth
                    name="country"
                    placeholder="Enter Country"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 1 }}
                    value={formData.country}
                    onChange={handleChange}
                    disabled
                    helperText="Your country (managed through your account settings)"
                  />
                </Grid>
              </Grid>

              {/* Fourth Row - Gender */}
              <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography sx={{ textAlign: "left", mb: 1 }}>Gender</Typography>
                  <TextField
                    fullWidth
                    select
                    name="gender"
                    placeholder="Select Gender"
                    variant="outlined"
                    margin="dense"
                    size="small"
                    sx={{ mb: 1 }}
                    value={formData.gender}
                    onChange={handleChange}
                    SelectProps={{
                      native: true,
                    }}
                    helperText="Select your gender"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </TextField>
                </Grid>
              </Grid>

              {/* Email and Website Visibility Checkbox */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  sx={{ textAlign: "left" }}
                  className='formCheckControl'
                  control={
                    <Checkbox 
                      checked={formData.mobile_email_flag}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          mobile_email_flag: e.target.checked
                        }));
                      }}
                    />
                  }
                  label="Do you want the email and phone number to be visible on the Certificate? If Yes, Please tick the checkbox."
                />
              </Box>

              {/* Save Changes Button */}
              <Box sx={{ mt: 4, textAlign: 'left' }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    background: 'linear-gradient(to right, #214F7D, #3A8139)',
                    color: 'white',
                    borderRadius: "50px",
                    px: 3,
                    py: 1,
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    '&:hover': {
                      background: 'linear-gradient(to right, #1a3f63, #2d6a2e)',
                    },
                    '&:disabled': {
                      background: '#ccc',
                      color: '#666'
                    }
                  }}
                >
                  {isSubmitting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} color="inherit" />
                      Saving Changes...
                    </Box>
                  ) : (
                    previewImage ? 'Save Changes' : 'Save Changes'
                  )}
                </Button>
              </Box>
            </form>
          </Grid>
        </Grid>

      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Loading profile information...
          </Typography>
        </Box>
      )}

      {/* No Data Available Message */}
      {(!user || Object.keys(user).length === 0) && !isLoading && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No profile information available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please log in again to refresh your profile data
          </Typography>
        </Box>
      )}

      {/* Success Message */}
      {submitMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {submitMessage}
        </Alert>
      )}

      {/* Error Message */}
      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submitError}
        </Alert>
      )}
    </Box>
  );
};

export default EditProfile;